import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ModalService } from 'projects/shared/modal/modal.service';
import { Borrower } from '../../../models/borrower-model';
import { Employment } from '../../../models/employment-model';
import { CategoryOfIncome } from '../../../models/enums/category-of-income.enum';
import { Income } from '../../../models/income-model';
import { EmploymentAndIncomeType, EmploymentDialogComponent } from '../employment-dialog/employment-dialog.component';
import { IncomeStep } from '../../../models/wizard/income-step.model';
import { IncomeStepConfig } from '../../../models/wizard/config/income-step-config.model';
import { SimpleIncomeDialogComponent } from '../simple-income-dialog/simple-income-dialog.component';
import { BorrowerIncomeComponent } from '../../borrower-income/borrower-income.component';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import * as _ from 'lodash';
import { TypeOfIncome } from '../../../models/enums/type-of-income.enum';
import { EmploymentTypeEnum } from '../../../models/mortgage.model';
import { MonthsToYearsAndMonthsPipe } from 'projects/shared/pipes/months-to-years-and-months.pipe';
import { INCOME_CATEGORY_OPTIONS } from './income-category-options';
import { LocalizedEditService } from '../../../transloco/localized-edit.service';
import { NgForm } from '@angular/forms';
import { Observer } from 'rxjs';
import { MortgageApplication } from '../../../models/mortgage-app.model';
import { getErrorMessageOrDefault } from 'projects/shared/utils/error.utils';
import { employmentHistoryInMonths } from '../../../utils/employment-duration.utils';
import { ErrorMessage } from '../../../models/error-message.model';
import { MortgageApplicationService } from '../../../services/mortgage-application.service';

declare const Swal: any;

const modalOptions: NgbModalOptions = {
  size: "xl",
  backdrop: "static",
  container: "body"
};

@Component({
    selector: 'income-step',
    templateUrl: 'income-step.component.html',
    styleUrls: ['./income-step.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class IncomeStepComponent
  extends WizardStepComponentBase<IncomeStep, IncomeStepConfig>
  implements OnInit, AfterViewInit
{
  @ViewChildren('borrowerIncome') borrowerIncomeComponents:
    | QueryList<BorrowerIncomeComponent>
    | undefined;

  @ViewChild('incomeForm')
  incomeForm: NgForm;

  // Single access point for the "enforce 2 years employment history" flag. The authorable
  // StepConfig is the single source of truth (config-side-by-side refactor); the runtime step
  // no longer carries this field. Defaults to false for legacy configs that predate it.
  get enforce2Years(): boolean {
    return this.config?.enforceAtLeast2YearsOfEmploymentHistory ?? false;
  }
  set enforce2Years(value: boolean) {
    if (this.config) {
      this.config.enforceAtLeast2YearsOfEmploymentHistory = value;
    }
  }

  borrowers: Borrower[] = [];

  mainBorrower!: Borrower;
  coBorrowers: Borrower[] = [];

  protected selectedTypeOfIncome: string;

  protected addingIncome: boolean = false;

  protected mortgage: MortgageApplication;

  protected readonly typesOfIncome = INCOME_CATEGORY_OPTIONS;

  borrowerEmploymentInfoByBorrowerId: Map<number, BorrowerEmploymentInfo> =
    new Map<number, BorrowerEmploymentInfo>();

  constructor(
    private readonly _modalService: ModalService,
    private readonly _mortgageService: MortgageApplicationService,
    private readonly _injector: Injector,
    private readonly _x: MonthsToYearsAndMonthsPipe,
    private readonly _cdr: ChangeDetectorRef,
    public readonly i18n: LocalizedEditService
  ) {
    super(_injector);
    this.saveMortgageApplicationBeforeNextStep = true;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initialize();
  }

  onNextClicked() {
    if (this.wizardFlowService.isEditEnabled) {
      super.onNextClicked();
      return;
    }
    if (this.incomeForm) {
      this.incomeForm.form.markAllAsTouched();
      if (!this.incomeForm.form.valid) {
        return;
      }
    }
    this.proceedToNextStep();
  }

  onPensionClicked = () => {
    if (!this.inEditMode) {
      this.showPensionIncomePopup();
    }
  };

  onW2BasedEmploymentClicked = () => {
    if (!this.inEditMode) {
      this.showW2BasedEmploymentPopup();
    }
  };

  onMilitaryPayClicked = () => {
    if (!this.inEditMode) {
      this.showMilitaryPayPopup();
    }
  };

  onIndependentContractorClicked = () => {
    if (!this.inEditMode) {
      this.showIndependentContractorPopup();
    }
  };

  onRentalIncomeClicked = () => {
    if (!this.inEditMode) {
      this.showRentalIncomePopup();
    }
  };

  onSocialSecurityClicked = () => {
    if (!this.inEditMode) {
      this.showSocialSecurityIncomePopup();
    }
  };

  onSelfEmploymentClicked = () => {
    if (!this.inEditMode) {
      this.showBusinessSelfEmploymentPopup();
    }
  };

  onOtherIncomeClicked = () => {
    if (!this.inEditMode) {
      this.showOtherIncomePopup();
    }
  };

  onIncomesChanged = () => {
    this.calculateEmploymentTotalDurationInMonthsForBorrowers();
    // Refresh every per-borrower income section. Needed when a user reassigns
    // an employment to a different borrower: the source and destination
    // sections both need to recompute their income lists.
    this.borrowerIncomeComponents?.forEach((c) => c.refresh());
    this._cdr.markForCheck();
  };

  onTypeOfIncomeChanged = () => {
    switch (this.selectedTypeOfIncome) {
      case TypeOfIncome.Base:
        this.showW2BasedEmploymentPopup();
        return;
      case TypeOfIncome.ContractBasis:
        this.showIndependentContractorPopup();
        return;
      case TypeOfIncome.MilitaryBasePay:
        this.showMilitaryPayPopup();
        return;
      case TypeOfIncome.NetRentalIncome:
        this.showRentalIncomePopup();
        return;
      case TypeOfIncome.SocialSecurity:
        this.showSocialSecurityIncomePopup();
        return;
      case TypeOfIncome.Pension:
        this.showPensionIncomePopup();
        return;
      case TypeOfIncome.SelfEmploymentIncome:
        this.showBusinessSelfEmploymentPopup();
        return;
      case TypeOfIncome.OtherTypesOfIncome:
        this.showOtherIncomePopup();
        return;
    }
  };

  onAddIncomeClicked = () => {
    this.addingIncome = true;
  };

  private initialize = () => {
    const applicationId = this.mortgageApplication.applicationId;

    const observer: Observer<MortgageApplication> = {
      next: (mortgage: MortgageApplication) => {
        this.mortgage = mortgage;
        this.wizardFlowService.updateContext(mortgage);

        this.borrowers = this.mortgage.borrowers;
        this.migrateIncomeCategoriesToIncomeTypes(this.borrowers);
        if (this.wizardFlowService.inCoApplyFlow) {
          this.borrowers = [this.wizardFlowService.context.currentBorrower];
        }
        if (this.currentBorrower) {
          this.mainBorrower = this.currentBorrower;
          this.coBorrowers = this.mortgage.borrowers.filter(
            (b) =>
              b.primaryEmail.toLocaleLowerCase() !==
              this.mainBorrower.primaryEmail.toLocaleLowerCase()
          );
        }

        this.calculateEmploymentTotalDurationInMonthsForBorrowers();
        this.stopSpinner();
        this._cdr.detectChanges();
      },
      error: (err: any) => {
        const errorMessage = getErrorMessageOrDefault(err, {
          defaultMessage: 'Error getting the employment/income info.',
        });
        this.showError(
          new ErrorMessage(
            'Error getting the employment/income info.',
            errorMessage
          )
        );
        this.stopSpinner();
        this._cdr.detectChanges();
      },
      complete: () => {},
    };
    this.startSpinner();
    this._mortgageService.getMortgage(applicationId, false).subscribe(observer);
  };

  private proceedToNextStep = () => {
    let hasEnoughEmploymentHistory = true;

    const borrowerEmploymentDurationsHtml: string[] = [];

    this.mortgage.borrowers.forEach((borrower) => {
      if (
        this.borrowerSetting(borrower) ||
        this.mainBorrower.borrowerId === borrower.borrowerId
      ) {
        if (
          this.borrowerEmploymentInfoByBorrowerId.has(borrower.borrowerId)
        ) {
          const borrowerEmploymentInfo =
            this.borrowerEmploymentInfoByBorrowerId.get(borrower.borrowerId);

          if (
            !borrowerEmploymentInfo.isRetired &&
            !this.checkIfBorrowerHasQualifiedIncomeToBypassEmploymentHistory(borrower) &&
            borrowerEmploymentInfo.numberOfMonthsEmployed < 24
          ) {
            hasEnoughEmploymentHistory = false;

            const employmentDurationText = this._x.transform(
              borrowerEmploymentInfo.numberOfMonthsEmployed
            );
            borrowerEmploymentDurationsHtml.push(`
              <div class='row mb-2'>
                <div class='col-6'>${
                  borrower.firstName + ' ' + borrower.lastName
                }</div>
                <div class='col-6' style='color:red'>${employmentDurationText}</div>
              <div>`);
          }
        }
      }
    });

    if (!hasEnoughEmploymentHistory) {
      if (this.enforce2Years) {
        let html = `<div class='mb-4'>${this.translocoService.translate('wizard.income.mustProvide2Years')}</div>`;
        borrowerEmploymentDurationsHtml.forEach((b) => {
          html += b;
        });

        const swalConfig = {
          title: this.translocoService.translate('common.warning'),
          html: html,
          icon: 'error',
          confirmButtonText: this.translocoService.translate('common.ok'),
          width: '700px',
        };
        Swal.fire(swalConfig).then(() => {});
      } else {
        let html = `<div class='mb-4'>${this.translocoService.translate('wizard.income.notProvided2Years')}</div>`;
        borrowerEmploymentDurationsHtml.forEach((b) => {
          html += b;
        });

        const swalConfig = {
          showCancelButton: true,
          title: this.translocoService.translate('common.warning'),
          html: html,
          icon: 'question',
          width: '700px',
          confirmButtonText: this.translocoService.translate('wizard.income.yesProceed'),
          cancelButtonText: this.translocoService.translate('wizard.income.noIWillAddThem'),
        };
        Swal.fire(swalConfig).then((result) => {
          if (result.value) {
            if (this.isValid()) {
              this.prepareEmploymentsAndIncomesForSave();
              super.onNextClicked();
            }
          }
        });
      }
    }

    if (hasEnoughEmploymentHistory && this.isValid()) {
      this.prepareEmploymentsAndIncomesForSave();
      super.onNextClicked();
    }
  };

  private prepareEmploymentsAndIncomesForSave = () => {
    // For every employment and their incomes, if the Ids are not set, meaning 0 or null, remove the modelGuid property from them
    // so that the backend can generate new Ids for them
    this.mortgage.borrowers.forEach((borrower) => {
      if (
        this.borrowerSetting(borrower) ||
        this.mainBorrower.borrowerId === borrower.borrowerId
      ) {
        borrower.employments.forEach((employment) => {
          if (!employment.employmentId) {
            delete employment.modelGuid;
          }
          employment.incomes.forEach((income) => {
            if (!income.incomeId) {
              delete income.modelGuid;
            }
          });
        });
        // Do the same thing for non-emplyment incomes
        borrower.nonEmploymentIncomes.forEach((income) => {
          if (!income.incomeId) {
            delete income.modelGuid;
          }
        });
      }
    });
  };

  private getBorrowersWithoutEmployment = (): Borrower[] => {
    const borrowersWithoutEmployment: Borrower[] = [];
    this.mortgage.borrowers.forEach((borrower) => {
      if (
        this.borrowerSetting(borrower) ||
        this.mainBorrower.borrowerId === borrower.borrowerId
      ) {
        if (!borrower.employments || !borrower.employments.length) {
          borrowersWithoutEmployment.push(borrower);
        }
      }
    });
    return borrowersWithoutEmployment;
  };

  private prepareHtmlForBorrowersWithoutEmploymentNotice = (
    borrowers: Borrower[]
  ): string[] => {
    const borrowersWithoutEmploymentHtml: string[] = [];
    borrowers.forEach((borrower) => {
      borrowersWithoutEmploymentHtml.push(`
          <div class='row mb-2' style='max-width: 350px; margin: 0 auto'>
            <div class='col-12'><strong>${this.utilsService.getPersonsFullName(
              borrower
            )}</strong></div>
          <div>`);
    });
    return borrowersWithoutEmploymentHtml;
  };

  private isBorrowerRetired = (borrower: Borrower): boolean => {
    return (
      borrower.employments.filter(
        (emp) =>
          emp.employmentType == EmploymentTypeEnum.CurrentEmployer &&
          emp.employer == 'Retired'
      ).length > 0
    );
  };

  private checkIfBorrowerHasQualifiedIncomeToBypassEmploymentHistory = (borrower: Borrower): boolean => {
    if (borrower.nonEmploymentIncomes && borrower.nonEmploymentIncomes.length > 0) {
      const qualifiedIncomeTypes: TypeOfIncome[] = [
        TypeOfIncome.Retirement,
        TypeOfIncome.Pension,
        TypeOfIncome.SocialSecurity,
        TypeOfIncome.PublicAssistance,
        TypeOfIncome.Disability,
        TypeOfIncome.RoyaltyPayment,
        TypeOfIncome.ChildSupport,
        TypeOfIncome.SeparateMaintenance,
        TypeOfIncome.AlimonyChildSupport,
      ];

      return borrower.nonEmploymentIncomes.some(
        income => qualifiedIncomeTypes.includes(income.typeOfIncome)
      );
    }
    return false;
  };

  private isValid(): boolean {
    let result: boolean = true;
    this.borrowerIncomeComponents?.forEach((borrowerIncomeComponent) => {
      if (!borrowerIncomeComponent.isValid()) {
        result = false;
      }
    });
    return result;
  }

  private migrateIncomeCategoriesToIncomeTypes = (borrowers: Borrower[]) => {
    borrowers.forEach((b) => {
      b.nonEmploymentIncomes.forEach((i) => {
        if (i.categoryOfIncome && !i.typeOfIncome) {
          i.typeOfIncome = this.fromCategoryToType(i.categoryOfIncome);
        }
      });
    });
  };

  private fromCategoryToType = (category: CategoryOfIncome): TypeOfIncome => {
    switch (category) {
      case CategoryOfIncome.Pension:
        return TypeOfIncome.Pension;
      case CategoryOfIncome.Rental:
        return TypeOfIncome.NetRentalIncome;
      case CategoryOfIncome.IndependentContractor:
        return TypeOfIncome.ContractBasis;
      case CategoryOfIncome.MilitaryPay:
        return TypeOfIncome.MilitaryBasePay;
      case CategoryOfIncome.SocialSecurity:
        return TypeOfIncome.SocialSecurity;
      case CategoryOfIncome.BusinessSelfEmployment:
        return TypeOfIncome.SelfEmploymentIncome;
      case CategoryOfIncome.Other:
        return TypeOfIncome.OtherTypesOfIncome;
      default:
        return TypeOfIncome.OtherTypesOfIncome;
    }
  };

  /**
   * Returns the list of borrowers who are applying on this application.
   * Exposed as a public method so the template can bind to it (used to
   * populate the borrower picker on the employment edit dialog).
   */
  public getBorrowersAppliedFor() {
    const borrowersAppliedFor: Borrower[] = [];
    borrowersAppliedFor.push(this.currentBorrower);
    this.mortgage.borrowers.forEach((borrower) => {
      const result = this.wizardFlowService.context.borrowerSettings.get(
        borrower.borrowerId
      );
      if (result) {
        borrowersAppliedFor.push(borrower);
      }
    });
    return borrowersAppliedFor;
  }

  private showOtherIncomePopup(income?: Income) {
    this.showNonEmploymentBasedIncomeDialog(
      TypeOfIncome.OtherTypesOfIncome,
      income
    );
  }

  private showPensionIncomePopup(income?: Income) {
    this.showNonEmploymentBasedIncomeDialog(TypeOfIncome.Pension, income);
  }

  private showSocialSecurityIncomePopup(income?: Income) {
    this.showNonEmploymentBasedIncomeDialog(
      TypeOfIncome.SocialSecurity,
      income
    );
  }

  private showRentalIncomePopup(income?: Income) {
    this.showNonEmploymentBasedIncomeDialog(
      TypeOfIncome.NetRentalIncome,
      income
    );
  }

  private showBusinessSelfEmploymentPopup(employment?: Employment) {
    this.showEmploymentBasedIncomeDialog(
      TypeOfIncome.SelfEmploymentIncome,
      employment
    );
  }

  private showMilitaryPayPopup(employment?: Employment) {
    this.showEmploymentBasedIncomeDialog(
      TypeOfIncome.MilitaryCombatPay,
      employment
    );
  }

  private showIndependentContractorPopup(employment?: Employment) {
    this.showEmploymentBasedIncomeDialog(
      TypeOfIncome.ContractBasis,
      employment
    );
  }

  private showEmploymentBasedIncomeDialog = (
    typeOfIncome: TypeOfIncome,
    employment?: Employment
  ) => {
    const modal = this._modalService.show(
      EmploymentDialogComponent,
      modalOptions
    );
    modal.componentInstance.typeOfIncome = typeOfIncome;
    modal.componentInstance.showCountry = true;
    if (employment) {
      modal.componentInstance.employment = employment;
    } else {
      if (typeOfIncome == TypeOfIncome.SelfEmploymentIncome) {
        modal.componentInstance.employment.selfEmployed = true;
      }
    }
    modal.componentInstance.fieldsToConfig = this.step.fieldConfig;
    modal.componentInstance.borrowers = this.wizardFlowService.inCoApplyFlow
      ? [this.currentBorrower]
      : this.getBorrowersAppliedFor();
    modal.result.then(
      (result: EmploymentAndIncomeType) => {
        this.addingIncome = false;
        this.selectedTypeOfIncome = null;
        if (result.typeOfIncome === TypeOfIncome.Disability) {
          // This is not an employment income - add it as non-employment income
          const disability = new Income();
          disability.borrowerId = result.employment.borrowerId;
          disability.typeOfIncome = TypeOfIncome.Disability;
          disability.monthlyIncome = result.employment.monthlyIncome;
          this.onSaveClickedOnIncomeModalForCreate(disability);
          return;
        }
        this.onSaveClickedOnEmploymentModalForCreate(
          result.employment,
          typeOfIncome
        );
      },
      (error) => {
        this.addingIncome = false;
        this.selectedTypeOfIncome = null;
      }
    );
  };

  private showNonEmploymentBasedIncomeDialog = (
    typeOfIncome: TypeOfIncome,
    income?: Income
  ) => {
    const modal = this._modalService.show(SimpleIncomeDialogComponent);
    modal.componentInstance.header = this.getDialogHeader(typeOfIncome);
    modal.componentInstance.label =
      this.getLabel('income.monthlyIncome', this.translocoService.translate('wizard.income.monthlyPay'));
    if (income) {
      modal.componentInstance.income = income;
    }
    modal.componentInstance.editorIncomeType = typeOfIncome;
    modal.componentInstance.fieldsToConfig = this.step.fieldConfig;
    modal.componentInstance.borrowers = this.wizardFlowService.inCoApplyFlow
      ? [this.currentBorrower]
      : this.getBorrowersAppliedFor();
    modal.result.then(
      (income) => {
        this.addingIncome = false;
        this.selectedTypeOfIncome = null;
        this.onSaveClickedOnIncomeModalForCreate(income);
      },
      (error) => {
        this.addingIncome = false;
        this.selectedTypeOfIncome = null;
      }
    );
  };

  private getDialogHeader = (typeOfIncome: TypeOfIncome): string => {
    const t = (key: string) => this.translocoService.translate(key);
    switch (typeOfIncome) {
      case TypeOfIncome.Pension:
        return t('wizard.income.pension');
      case TypeOfIncome.NetRentalIncome:
        return t('wizard.income.rental');
      case TypeOfIncome.SocialSecurity:
        return t('wizard.income.socialSecurity');
      case TypeOfIncome.MilitaryBasePay:
        return t('wizard.income.militaryPay');
      case TypeOfIncome.Base:
        return t('wizard.income.employmentW2');
      case TypeOfIncome.SelfEmploymentIncome:
        return t('wizard.income.businessSelfEmployment');
      case TypeOfIncome.ContractBasis:
        return t('wizard.income.independentContractor');
      case TypeOfIncome.OtherTypesOfIncome:
        return t('wizard.income.other');
      default:
        return t('wizard.income.incomeDefault');
    }
  };

  private showW2BasedEmploymentPopup(employment?: Employment) {
    this.showEmploymentBasedIncomeDialog(TypeOfIncome.Base, employment);
  }

  private onSaveClickedOnIncomeModalForCreate = (income: Income): void => {
    const borrower = this.borrowers.find(
      (b) => b.borrowerId == income.borrowerId
    );
    if (borrower) {
      borrower.nonEmploymentIncomes.push(income);
      if (this.borrowerIncomeComponents) {
        const borrowerIncomeComponent = this.borrowerIncomeComponents.find(
          (c) => c.borrower.borrowerId == income.borrowerId
        );
        if (borrowerIncomeComponent) {
          borrowerIncomeComponent.refresh();
        }
      }
    }
    // Re-derive each borrower's `BorrowerEmploymentInfo` — specifically the
    // `hasQualifiedIncomeForBypass` flag, which gates the red duration
    // indicator. Adding a Pension/SocialSecurity/etc. income here must flip
    // that flag so the indicator stops rendering red.
    this.calculateEmploymentTotalDurationInMonthsForBorrowers();
    this._cdr.markForCheck();
  };

  private onSaveClickedOnEmploymentModalForCreate = (
    employment: Employment,
    typeOfIncome: TypeOfIncome
  ): void => {

    if (typeOfIncome !== TypeOfIncome.SelfEmploymentIncome) {
      const income: Income = new Income();
      income.monthlyIncome = Number(employment.monthlyIncome || employment.selfEmploymentMonthlyIncomeOrLoss);
      income.typeOfIncome = typeOfIncome;
      income.businessType = employment.businessType;
      if (income.typeOfIncome === TypeOfIncome.ContractBasis) {
        income.categoryOfIncome = CategoryOfIncome.IndependentContractor;
      }
      if (income.typeOfIncome === TypeOfIncome.MilitaryBasePay) {
        income.categoryOfIncome = CategoryOfIncome.MilitaryPay;
      }

      employment.incomes.push(income);
    }

    const borrower = this.borrowers.find(
      (b) => b.borrowerId == employment.borrowerId
    );
    if (borrower) {
      borrower.employments.push(employment);
    }

    if (this.borrowerIncomeComponents) {
      const borrowerIncomeComponent = this.borrowerIncomeComponents.find(
        (c) => c.borrower.borrowerId == employment.borrowerId
      );
      if (borrowerIncomeComponent) {
        borrowerIncomeComponent.refresh();
      }
    }
    this.calculateEmploymentTotalDurationInMonthsForBorrowers();
    this._cdr.markForCheck();
  };

  private calculateEmploymentTotalDurationInMonthsForBorrowers = () => {
    this.mortgage.borrowers.forEach((borrower) => {
      // Overlapping jobs count once, gaps between jobs don't count.
      const totalDurationOfEmploymentInMonths = employmentHistoryInMonths(borrower.employments);
      const isRetired = !!borrower.employments?.length && this.isBorrowerRetired(borrower);
      const borrowerEmployemntInfo: BorrowerEmploymentInfo = {
        numberOfMonthsEmployed: totalDurationOfEmploymentInMonths,
        isRetired: isRetired,
        hasQualifiedIncomeForBypass: this.checkIfBorrowerHasQualifiedIncomeToBypassEmploymentHistory(borrower),
      };
      this.borrowerEmploymentInfoByBorrowerId.set(
        borrower.borrowerId,
        borrowerEmployemntInfo
      );
    });
  };

  public hasIncome(borrowerId: number): boolean {
    if (this.borrowerIncomeComponents) {
      const borrowerIncomeComponent = this.borrowerIncomeComponents.find(
        (c) => c.borrower.borrowerId == borrowerId
      );
      if (borrowerIncomeComponent) {
        return borrowerIncomeComponent.incomes.length > 0;
      }
    }
    return false;
  }
}

export class BorrowerEmploymentInfo {
  isRetired: boolean = false;
  numberOfMonthsEmployed: number;
  // True when the borrower has a non-employment income type (Retirement,
  // Pension, SocialSecurity, etc.) that bypasses the 2-year employment-history
  // rule. Drives the duration-indicator color in the template so a borrower
  // who's covered by qualifying income doesn't show red even with 0 months
  // of employment history. Kept in sync with mortgage state via the recalc
  // that runs on every income mutation (employment or non-employment).
  hasQualifiedIncomeForBypass: boolean = false;
}

